/**
 * ReceiptScanPage — M13a-2 Capture-UI + Storage-Upload + Scan-Liste
 *
 * Provides a native camera/file-capture flow for receipt images and shows the
 * list of the user's existing scanned receipts with their extraction status.
 *
 * Access gate: `canUseReceiptScan` (config/receiptScanAccess.ts)
 *   - Fail-closed: no network requests unless the user's UID is on the
 *     Beta-Allowlist OR the general flag is on AND the user is Premium.
 *   - M-UX-G2 (Growth-Audit Befund #4): when the general flag is on but the
 *     user isn't (yet) entitled, the page renders a `FeatureTeaser` upsell
 *     instead of `return null` — a Free user must be able to SEE the scanner
 *     exists before they can want it. The Beta-Allowlist-off case (flag off,
 *     not on the allowlist) still renders `null` — Beta access is a
 *     controlled trial, not a purchase incentive (architecture-ux-restructure
 *     §4.1 `locked`-Regel: Beta-Gates bleiben `hidden`, nicht `locked`).
 *
 * Capture: native `<input type="file" accept="image/*" capture="environment">`,
 * no jscanify / no client-side image processing (§8.4 MVP-Scope).
 *
 * Upload path: `uploadReceiptImage` → Storage `receipts/{userId}/{receiptId}.ext`
 * → caller then invokes `extractReceipt` Callable (M13a-3, not yet implemented).
 *
 * @module ReceiptScanPage
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconScan, IconImage, IconError, IconSpinner, IconSuccess, IconRejected } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { uploadReceiptImage } from '../api/receiptUploadApi';
import { callExtractReceipt } from '../api/extractReceiptApi';
import { getScannedReceiptsForUser } from '../api/scannedReceiptsApi';
import { ScannedReceipt } from '../types/scannedReceiptTypes';
import ReceiptScanModal from '../components/expenses/ReceiptScanModal';
import ReceiptScanConsentGate from '../components/expenses/ReceiptScanConsentGate';
import logger from '../utils/logger';
import {
  canUseReceiptScan,
  RECEIPT_SCAN_FLAG,
  hasReceiptScanAvvConsent,
} from '../config/receiptScanAccess';
import { resolveTier } from '../config/entitlements';
import MessageContextButton from '../components/messaging/MessageContextButton';
import FeatureTeaser from '../components/premium/FeatureTeaser';

// ─── Status helpers ───────────────────────────────────────────────────────────

type ReceiptStatus = ScannedReceipt['status'];

interface StatusDisplay {
  labelKey: string;
  colorClass: string;
  Icon: React.ComponentType<{ className?: string; size?: number }>;
}

const STATUS_DISPLAY: Record<ReceiptStatus, StatusDisplay> = {
  extracting: {
    labelKey: 'receiptScan.statusExtracting',
    colorClass: 'text-[var(--tone-warning-fg)]',
    Icon: IconSpinner,
  },
  extracted: {
    labelKey: 'receiptScan.statusExtracted',
    colorClass: 'text-[var(--tone-success-fg)]',
    Icon: IconSuccess,
  },
  failed: {
    labelKey: 'receiptScan.statusFailed',
    colorClass: 'text-[var(--tone-danger-fg)]',
    Icon: IconRejected,
  },
  booked: {
    labelKey: 'receiptScan.statusBooked',
    colorClass: 'text-accent-ink',
    Icon: IconSuccess,
  },
  discarded: {
    labelKey: 'receiptScan.statusDiscarded',
    colorClass: 'text-faint',
    Icon: IconRejected,
  },
};

// ─── ReceiptStatusBadge ───────────────────────────────────────────────────────

interface ReceiptStatusBadgeProps {
  status: ReceiptStatus;
}

const ReceiptStatusBadge: React.FC<ReceiptStatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation('common');
  const { labelKey, colorClass, Icon } = STATUS_DISPLAY[status];
  return (
    <span className={`flex items-center gap-1 text-sm font-medium ${colorClass}`}>
      <Icon size={12} className={status === 'extracting' ? 'animate-spin' : ''} />
      {t(labelKey)}
    </span>
  );
};

// ─── ReceiptScanPage ──────────────────────────────────────────────────────────

/**
 * Capture + list page for the M13a Belegscanner feature.
 *
 * Per-user access gate (fail-closed): the feature UI is completely inert
 * (no network requests) unless `canUseReceiptScan` grants access — i.e. the
 * user's UID is on the Beta-Allowlist OR the general flag is on AND the user
 * is Premium. `useAuth` is called unconditionally before the gate so Hook
 * order stays stable; all feature Hooks live in `ReceiptScanPageInner`,
 * mounted only on access.
 *
 * fix-advisor-nav-curation (Abschnitt E): im Advisor-Kontext (`activeRole==='advisor'`)
 * wird die eigene Scan-/Premium-Gate umgangen — der StB sieht eine results-only-Ansicht
 * (nur gebuchte Belege, keine Capture-/Upload-Mechanik, kein Buchen). Dieser Zweig
 * greift VOR dem `canUseReceiptScan`-Gate, damit der StB unabhängig vom eigenen
 * Beta-/Premium-Status Zugriff hat.
 *
 * M-UX-G2 (Growth-Audit Befund #4): when access is denied, the fallback is no
 * longer unconditionally `return null`. `canUseReceiptScan` already covers
 * BOTH unlock paths (Beta-Allowlist OR flag&&premium) — so if it's `false`
 * and `RECEIPT_SCAN_FLAG` is `true`, the user is by construction NOT on the
 * Beta-Allowlist (that branch would already have returned `true`) and simply
 * lacks Premium: exactly the "generally rolled out, not yet paid" case the
 * `FeatureTeaser` targets. If the flag itself is off, nobody outside the
 * Beta-Allowlist can ever unlock the feature — `return null` stays correct
 * there (a locked door nobody can open yet is not a purchase moment).
 */
const ReceiptScanPage: React.FC = () => {
  const { currentUser, sellerData } = useAuth();
  const isAdvisorViewingClient = useIsReadOnly();
  if (isAdvisorViewingClient) {
    return <ReceiptScanPageInner readOnly />;
  }
  if (canUseReceiptScan(currentUser?.uid, resolveTier(sellerData))) {
    return <ReceiptScanPageInner />;
  }
  if (RECEIPT_SCAN_FLAG) {
    return <ReceiptScanTeaser />;
  }
  return null;
};

/**
 * M-UX-G2 upsell teaser shown when `RECEIPT_SCAN_FLAG` is on but the current
 * user isn't (yet) entitled (see gate comment on `ReceiptScanPage`). Purely
 * presentational — no Firestore reads, no Hooks beyond `FeatureTeaser`'s own
 * `useTranslation`/`useNavigate`.
 *
 * M-N5 (architecture-nudge.md §7.4): `promotionId="feature_teaser_receipt_scan"`
 * mit `creativeSlot="page"` — unterscheidet diese Fläche von der gleichnamigen
 * Promotion in `components/inbox/ScanTeaserCard.tsx` (`creativeSlot="inbox_stream"`).
 *
 * Ziel-Tier **Ultra** (Korrektur 2026-07-29): `canUseReceiptScan` verlangt
 * `tier === 'ultra'` (`receiptScanAccess.ts`, GA §3.2) — der Teaser bewarb aber
 * weiterhin „Premium", d. h. ein Premium-Kauf hätte den Scanner nicht
 * freigeschaltet (§5 UWG). Badge/CTA/Ziel-Route folgen jetzt dem
 * Bank-Abgleich-Muster. Der identische Text in `ScanTeaserCard` ist
 * gleichlautend nachgezogen (beide Flächen teilen die i18n-Keys).
 */
const ReceiptScanTeaser: React.FC = () => (
  <FeatureTeaser
    icon={<IconScan size={30} aria-hidden />}
    badgeKey="receiptScan.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="receiptScan.teaserTitle"
    titleDefault="KI-Beleg-Scanner"
    descriptionKey="receiptScan.teaserDescription"
    descriptionDefault="Fotografieren Sie Belege mit dem Handy — die KI liest Betrag, Lieferant und Datum automatisch aus und bucht sie direkt als Ausgabe. Schluss mit Zettelwirtschaft."
    highlights={[
      {
        labelKey: 'receiptScan.teaserHighlight1',
        defaultLabel: 'Beleg fotografieren statt manuell erfassen',
      },
      {
        labelKey: 'receiptScan.teaserHighlight2',
        defaultLabel: 'KI erkennt Betrag, Lieferant und Datum automatisch',
      },
      {
        labelKey: 'receiptScan.teaserHighlight3',
        defaultLabel: 'Direkt als Ausgabe buchen — in Sekunden',
      },
    ]}
    ctaKey="receiptScan.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_receipt_scan"
    creativeSlot="page"
  />
);

interface ReceiptScanPageInnerProps {
  /**
   * fix-advisor-nav-curation: results-only-Modus für den Advisor-Kontext.
   * Blendet Capture-/Upload-Zone und „Buchen"-CTA aus, öffnet das Buchungs-Modal
   * nie und filtert die Liste auf `status === 'booked'`. Default `false`
   * (unverändertes Verhalten für Owner/Beta-Zugriff).
   */
  readOnly?: boolean;
}

/**
 * Inner component rendered only when the user has access. Split to keep the
 * access gate outside the feature Hook-call boundary.
 */
const ReceiptScanPageInner: React.FC<ReceiptScanPageInnerProps> = ({ readOnly = false }) => {
  const { t } = useTranslation('common');
  const { currentUser, effectiveScopeUid, sellerData } = useAuth();

  // M13a-GA: gültige AVV-Zustimmung für die aktuelle Version? Fail-closed —
  // fehlt/veraltet ⇒ Scan gesperrt, das Zustimmungs-Panel wird statt der
  // Capture-Zone gerendert. Im readOnly-(Advisor-)Modus irrelevant (keine Capture).
  const avvAccepted = hasReceiptScanAvvConsent(sellerData?.receiptScanAvvAcceptedVersion);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Scan list state
  const [scans, setScans] = useState<ScannedReceipt[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Booking modal state — the receipt currently being reviewed (M13a-4).
  const [bookingReceipt, setBookingReceipt] = useState<ScannedReceipt | null>(null);

  // ─── Load scan list ──────────────────────────────────────────────────────────

  const loadScans = useCallback(async () => {
    if (!currentUser) return;
    setListLoading(true);
    setListError(null);
    try {
      // fix-advisor-nav-curation: effectiveScopeUid statt currentUser.uid — im
      // Advisor-Kontext ist currentUser.uid (StB) != effectiveScopeUid (Mandant);
      // getScannedReceiptsForUser wirft sonst (uid-mismatch-Guard). Für Owner ist
      // effectiveScopeUid === currentUser.uid → inert.
      const results = await getScannedReceiptsForUser(effectiveScopeUid);
      setScans(results);
    } catch (err) {
      logger.error(err, 'ReceiptScanPage: failed to load scanned receipts');
      setListError(t('receiptScan.listError'));
    } finally {
      setListLoading(false);
    }
  }, [currentUser, effectiveScopeUid, t]);

  useEffect(() => {
    void loadScans();
  }, [loadScans]);

  // ─── Booking modal handlers (M13a-4) ─────────────────────────────────────────

  /**
   * Opens the booking modal for an `extracted` scan (no-op for any other status).
   * fix-advisor-nav-curation: no-op im readOnly-Modus (defense-in-depth — der
   * auslösende CTA ist im Advisor-Kontext bereits ausgeblendet).
   */
  const handleOpenBooking = useCallback((scan: ScannedReceipt) => {
    if (readOnly) return;
    if (scan.status !== 'extracted') return;
    setBookingReceipt(scan);
  }, [readOnly]);

  /** Closes the modal and refreshes the list after a successful booking. */
  const handleBooked = useCallback(() => {
    setBookingReceipt(null);
    void loadScans();
  }, [loadScans]);

  // ─── File selection handler ──────────────────────────────────────────────────

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input so the same file can be re-selected after an error.
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (!currentUser) {
        setUploadError(t('receiptScan.errorAuth'));
        return;
      }

      // Harter Client-Guard (Defense-in-Depth): kein Upload/Scan ohne gültige
      // AVV-Zustimmung — verhindert auch den Storage-Upload vor der Zustimmung.
      if (!avvAccepted) {
        setUploadError(t('receiptScan.avv.required'));
        return;
      }

      setUploading(true);
      setUploadError(null);
      setUploadSuccess(null);

      try {
        const result = await uploadReceiptImage(file);
        logger.info('ReceiptScanPage: upload complete', result.storageRef);
        // Trigger server-side KI extraction — creates scanned_receipts/{receiptId}
        // (status extracted|failed). Without this the scan list never populates.
        const extraction = await callExtractReceipt({
          receiptId: result.receiptId,
          storageRef: result.storageRef,
          mimeType: result.mimeType,
          originalFilename: result.originalFilename,
        });
        logger.info('ReceiptScanPage: extraction finished', extraction.status);
        setUploadSuccess(t('receiptScan.uploadSuccess'));
        // Refresh the scan list to show the new entry created by extractReceipt.
        await loadScans();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error(err, 'ReceiptScanPage: upload error');

        if (message.includes('avv_consent_required')) {
          // Server-seitige AVV-Ablehnung (Defense-in-Depth-Sichtbarkeit): nie ein
          // generisches „Upload fehlgeschlagen".
          setUploadError(t('receiptScan.avv.required'));
        } else if (message.includes('MIME type') || message.includes('Unsupported')) {
          setUploadError(t('receiptScan.errorMime'));
        } else if (message.includes('too large') || message.includes('10 MB')) {
          setUploadError(t('receiptScan.errorSize'));
        } else if (message.includes('No authenticated user')) {
          setUploadError(t('receiptScan.errorAuth'));
        } else {
          setUploadError(t('receiptScan.errorUpload'));
        }
      } finally {
        setUploading(false);
      }
    },
    [currentUser, t, loadScans, avvAccepted],
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  // fix-advisor-nav-curation: im readOnly-Modus (Advisor-Kontext) nur die
  // gebuchten Belege anzeigen — der StB sieht ausschließlich die Buchungssicht,
  // keine offenen/fehlgeschlagenen Scans.
  const visibleScans = useMemo(
    () => (readOnly ? scans.filter((scan) => scan.status === 'booked') : scans),
    [scans, readOnly],
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-primary">{t('receiptScan.pageTitle')}</h1>
      </div>

      {/* M13a-GA AVV-Gate: ohne gültige Zustimmung wird ANSTELLE der Capture-Zone das
          Zustimmungs-Panel gerendert (die Scan-Liste darunter bleibt sichtbar —
          Bestandsscans sind kein Neu-Transfer). Nach Zustimmung rendert der nächste
          Zyklus (sellerData-Update) automatisch die Capture-Zone. Im readOnly-
          (Advisor-)Modus greift kein Gate (keine Capture-Mechanik). */}
      {!readOnly && !avvAccepted && (
        <div className="rounded-2xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] p-5">
          <ReceiptScanConsentGate onConsented={() => { /* sellerData-Update flippt avvAccepted */ }} />
        </div>
      )}

      {/* Capture / upload zone — fix-advisor-nav-curation: im readOnly-Modus (Advisor-
          Kontext) komplett ausgeblendet, der StB darf nicht scannen/hochladen.
          M13a-GA: nur bei gültiger AVV-Zustimmung. */}
      {!readOnly && avvAccepted && (
      <div className="space-y-4">
        <div
          role="button"
          tabIndex={0}
          aria-label={t('receiptScan.uploadPrompt')}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (!uploading && (e.key === 'Enter' || e.key === ' ')) {
              fileInputRef.current?.click();
            }
          }}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all select-none
            ${uploading
              ? 'border-indigo-500 bg-indigo-500/10 cursor-default'
              : 'border-strong hover:border-indigo-500 hover:bg-[rgb(var(--surface-raised-rgb)/0.5)]'
            }
          `}
        >
          {/* Native file input — hidden, triggered programmatically */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
            aria-hidden="true"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <IconSpinner size={30} className="text-accent-ink animate-spin" />
              <p className="text-primary text-sm font-medium">{t('receiptScan.uploading')}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-muted">
              <div className="flex items-center gap-3">
                <IconScan size={30} className="text-accent-ink" />
                <IconImage size={24} className="text-accent-ink" />
              </div>
              <div>
                <p className="text-primary font-medium">{t('receiptScan.uploadPrompt')}</p>
                <p className="text-sm mt-1">{t('receiptScan.uploadHint')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Upload success */}
        {uploadSuccess && !uploading && (
          <div className="flex items-start gap-2 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] rounded-lg p-3 text-sm text-[var(--tone-success-fg)]">
            <IconSuccess size={14} className="mt-0.5 flex-shrink-0" />
            <span>{uploadSuccess}</span>
          </div>
        )}

        {/* Upload error */}
        {uploadError && (
          <div className="flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">
            <IconError size={14} className="mt-0.5 flex-shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}
      </div>
      )}

      {/* Scan list */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-primary">{t('receiptScan.listTitle')}</h2>

        {listLoading && (
          <div className="flex items-center gap-2 text-muted text-sm">
            <IconSpinner className="animate-spin" />
            <span>{t('receiptScan.listLoading')}</span>
          </div>
        )}

        {listError && !listLoading && (
          <div className="flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">
            <IconError size={14} className="mt-0.5 flex-shrink-0" />
            <span>{listError}</span>
          </div>
        )}

        {!listLoading && !listError && visibleScans.length === 0 && (
          <p className="text-faint text-sm">{t('receiptScan.listEmpty')}</p>
        )}

        {!listLoading && visibleScans.length > 0 && (
          <ul className="space-y-3">
            {visibleScans.map((scan) => (
              <li
                key={scan.id}
                className="flex items-center justify-between gap-4 bg-[rgb(var(--surface-raised-rgb)/0.6)] border border-hairline rounded-xl px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-primary text-sm font-medium truncate">
                    {scan.originalFilename || scan.id}
                  </p>
                  {scan.extraction?.supplierName && (
                    <p className="text-muted text-xs truncate mt-0.5">
                      {scan.extraction.supplierName}
                    </p>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <ReceiptStatusBadge status={scan.status} />
                  {/* M-MSG-3: Nachrichten-Thread zum Beleg — additiv, sichtbar für BEIDE
                      Rollen (auch im readOnly-Advisor-Modus), nur hinter
                      canUseMessaging(effectiveScopeUid) (Button selbst rendert null
                      ohne Zugriff). */}
                  <MessageContextButton
                    context={{ type: 'receipt', refId: scan.id }}
                    refCollection="scanned_receipts"
                    contextSummary={{
                      title: scan.originalFilename || scan.id,
                      subtitle: scan.extraction?.supplierName ?? undefined,
                      badgeVariant: 'receipt',
                      hrefToRef: '/dashboard/belege',
                    }}
                  />
                  {scan.status === 'extracted' && !readOnly && (
                    <button
                      type="button"
                      onClick={() => handleOpenBooking(scan)}
                      className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors"
                    >
                      {t('receiptScan.bookCta')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Booking modal (M13a-4) — only mounted while an extracted scan is selected.
          fix-advisor-nav-curation: bleibt im readOnly-Modus immer null, da
          handleOpenBooking no-opt und der auslösende CTA ausgeblendet ist. */}
      {bookingReceipt && (
        <ReceiptScanModal
          open={bookingReceipt !== null}
          receipt={bookingReceipt}
          onClose={() => setBookingReceipt(null)}
          onBooked={handleBooked}
        />
      )}
    </div>
  );
};

export default ReceiptScanPage;
